import { FormField } from '@batiflow/ui';
import { Button, Input, __css, tokens } from '@fluentui/react-components';
import { Add20Regular, Dismiss16Regular } from '@fluentui/react-icons';
import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_GUEST_DOMAINS, checkDomain } from './model';

/** Codes, numbers and times read left to right, also in Arabic. */
const LTR = 'ltr';

const useStyles = __css({"root":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"add":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f6jr5hl","i8kkvl":"fsnqrgy","Belr9w4":"f1wn3y0l"},"field":{"Bh6795r":"fqerorx","xawz":"fuk1rnt","Bgto7am":"fuzgk9b","D305fb":"f1f0uekf"},"input":{"Bns2gvi":"f1c209jx","Bgto7am":"fuzgk9b"},"inputElement":{"Bgto7am":"fuzgk9b"},"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fgdu4nw","mc9l5x":"f22iagw","Eh141a":"fni485r","i8kkvl":"fsnqrgy","Belr9w4":"f1wn3y0l"},"chip":{"mc9l5x":"ftuwxu6","Bt984gj":"f122n59","i8kkvl":"f1ufnopg","B1hrpyx":"fmm5bso","Bnt446c":"fv8152r","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f18qryls","Be2twd7":"fkhj508","De3pzq":"f16xq7d1","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"f44lkw9"},"empty":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","sj55zd":"fkfq4zb"}});

export interface DomainListProps {
  domains: readonly string[];
  onChange: (domains: string[]) => void;
  /** Texts of the list when it is not the organisation's own (the guest domains of a space). */
  texts?: { label: string; hint: string; empty: string };
}

/** The allowed guest domains as a list of removable chips, with a field that checks each new one. */
export function DomainList({ domains, onChange, texts }: DomainListProps) {
  const { t } = useTranslation('web');
  const styles = useStyles();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const check = checkDomain(draft, domains);
    if (!check.ok) {
      setError(t(`policies.sharing.domains.errors.${check.reason}`, { max: MAX_GUEST_DOMAINS }));
      return;
    }
    onChange([...domains, check.domain]);
    setDraft('');
    setError(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Enter adds the domain; it must not submit the whole policies form.
    if (event.key !== 'Enter') return;
    event.preventDefault();
    add();
  };

  return (
    <div className={styles.root}>
      <div className={styles.add}>
        <FormField
          className={styles.field}
          label={texts?.label ?? t('policies.sharing.domains.label')}
          hint={texts?.hint ?? t('policies.sharing.domains.hint')}
          error={error}
        >
          <Input
            dir={LTR}
            className={styles.input}
            root={{ dir: LTR }}
            input={{ className: styles.inputElement }}
            inputMode="url"
            autoComplete="off"
            placeholder={t('policies.sharing.domains.placeholder')}
            value={draft}
            onChange={(_event, data) => {
              setDraft(data.value);
              setError(null);
            }}
            onKeyDown={onKeyDown}
            contentAfter={
              <Button appearance="transparent" size="small" icon={<Add20Regular />} onClick={add}>
                {t('policies.sharing.domains.add')}
              </Button>
            }
          />
        </FormField>
      </div>
      {domains.length === 0 ? (
        <p className={styles.empty}>{texts?.empty ?? t('policies.sharing.domains.empty')}</p>
      ) : (
        <ul className={styles.list} aria-label={t('policies.sharing.domains.listLabel')}>
          {domains.map((domain) => (
            <li key={domain} className={styles.chip}>
              <bdi dir={LTR}>{domain}</bdi>
              <Button
                appearance="subtle"
                size="small"
                icon={<Dismiss16Regular />}
                aria-label={t('policies.sharing.domains.remove', { domain })}
                onClick={() => {
                  onChange(domains.filter((item) => item !== domain));
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/admin/policies/domain-list.griffel.css";